import type { PriorityProposal } from '../types/prioritization/priorityProposal';
import { canUserSeeProposal } from '../types/prioritization/priorityProposal';
import type { PrioritizationTask } from '../types/prioritization/prioritizationTask';
import type { CatchballTierTurn } from '../types/prioritization/catchballTierTurn';
import { catchballLegacyRowId, catchballTurnRowId } from './catchballQueueRowIds';

export interface MyTasksQueueMetricsInput {
  tasks: PrioritizationTask[];
  userId: string | null | undefined;
  archivedIds: Set<string>;
  hiddenCatchballIds: Set<string>;
  activeTurns: Array<{ loopId: string; cycleId: string; turn: CatchballTierTurn }>;
  filteredLegacyLoops: ReadonlyArray<{ loopId: string }>;
  proposals: PriorityProposal[];
}

export interface MyTasksQueueMetrics {
  /** Open, assigned to me, not hidden from my list */
  openAssignedNotHidden: number;
  /** Open, created by me, not hidden from my list */
  openCreatedNotHidden: number;
  /** Distinct open tasks where I'm assignee or creator, not hidden (union, no double-count) */
  openUnionNotHidden: number;
  /** Catchball rows in my queue, not hidden from my list */
  visibleCatchballCount: number;
  /** Proposals where I'm tosser and review is needed */
  proposalReviewCount: number;
  /** Matches Tasks page: Assigned to me + Open + Active (tasks + Catchball) */
  mineOpenActiveQueue: number;
  /** Assigned by me + Open + Active (tasks only) */
  createdOpenActiveQueue: number;
  /** All tab + Open + Active */
  allOpenActiveQueue: number;
  /** Sidebar: inbox-style attention (queue + proposals to review) */
  sidebarAttentionCount: number;
}

export function getMyTasksQueueMetrics(input: MyTasksQueueMetricsInput): MyTasksQueueMetrics {
  const {
    tasks,
    userId,
    archivedIds,
    hiddenCatchballIds,
    activeTurns,
    filteredLegacyLoops,
    proposals,
  } = input;

  if (!userId) {
    return {
      openAssignedNotHidden: 0,
      openCreatedNotHidden: 0,
      openUnionNotHidden: 0,
      visibleCatchballCount: 0,
      proposalReviewCount: 0,
      mineOpenActiveQueue: 0,
      createdOpenActiveQueue: 0,
      allOpenActiveQueue: 0,
      sidebarAttentionCount: 0,
    };
  }

  let openAssignedNotHidden = 0;
  for (const t of tasks) {
    if (
      t.status === 'open' &&
      !archivedIds.has(t.id) &&
      t.participantIds?.includes(userId)
    ) {
      openAssignedNotHidden += 1;
    }
  }

  let openCreatedNotHidden = 0;
  for (const t of tasks) {
    if (t.status === 'open' && !archivedIds.has(t.id) && t.createdBy === userId) {
      openCreatedNotHidden += 1;
    }
  }

  const unionIds = new Set<string>();
  for (const t of tasks) {
    if (t.status !== 'open' || archivedIds.has(t.id)) continue;
    const mine = t.participantIds?.includes(userId);
    const created = t.createdBy === userId;
    if (mine || created) unionIds.add(t.id);
  }
  const openUnionNotHidden = unionIds.size;

  let visibleCatchballCount = 0;
  for (const { turn } of activeTurns) {
    if (!hiddenCatchballIds.has(catchballTurnRowId(turn.id))) visibleCatchballCount += 1;
  }
  for (const leg of filteredLegacyLoops) {
    if (!hiddenCatchballIds.has(catchballLegacyRowId(leg.loopId))) visibleCatchballCount += 1;
  }

  let proposalReviewCount = 0;
  for (const p of proposals) {
    if (
      canUserSeeProposal(p, userId) &&
      p.tosserId === userId &&
      (p.status === 'catchball' || p.status === 'under_review')
    ) {
      proposalReviewCount += 1;
    }
  }

  const mineOpenActiveQueue = openAssignedNotHidden + visibleCatchballCount;
  const createdOpenActiveQueue = openCreatedNotHidden;
  const allOpenActiveQueue = openUnionNotHidden + visibleCatchballCount;
  const sidebarAttentionCount = mineOpenActiveQueue + proposalReviewCount;

  return {
    openAssignedNotHidden,
    openCreatedNotHidden,
    openUnionNotHidden,
    visibleCatchballCount,
    proposalReviewCount,
    mineOpenActiveQueue,
    createdOpenActiveQueue,
    allOpenActiveQueue,
    sidebarAttentionCount,
  };
}
